import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import EntitlementCard from './EntitlementCard';
import { CANVAS_ID, CATEGORY_ORDER } from './planUtils';

// Right pane: "Included in <plan>". Drop target for library blocks; cards
// are sortable within it (order is cosmetic — the server stores the list).
const PlanCanvas = ({ planName, entitlements, featuresByKey, usageStats, errors, onChange, onRemove, t }) => {
  const { setNodeRef, isOver } = useDroppable({ id: CANVAS_ID });
  const known = entitlements.filter((e) => featuresByKey[e.featureKey]);
  const byCategory = CATEGORY_ORDER
    .map((category) => ({ category, items: known.filter((e) => featuresByKey[e.featureKey].category === category) }))
    .filter((g) => g.items.length > 0);

  return (
    <Paper ref={setNodeRef} variant="outlined" data-testid="plan-canvas" sx={{ p: 2, minHeight: 320, borderColor: isOver ? 'primary.main' : 'divider', height: '100%' }}>
      <Typography variant="overline" color="text.secondary">
        {t('Included in {{name}}', { name: planName, defaultValue: `Included in ${planName}` })}
      </Typography>
      <SortableContext items={known.map((e) => e.featureKey)} strategy={verticalListSortingStrategy}>
        {byCategory.map((group) => (
          <Box key={group.category} sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600, display: 'block', mb: 0.5 }}>
              {t(group.category, { defaultValue: group.category })}
            </Typography>
            {group.items.map((entitlement) => (
              <EntitlementCard
                key={entitlement.featureKey}
                feature={featuresByKey[entitlement.featureKey]}
                entitlement={entitlement}
                stats={usageStats?.[entitlement.featureKey]}
                error={errors[`limit:${entitlement.featureKey}`]}
                onChange={(next) => onChange(entitlement.featureKey, next)}
                onRemove={() => onRemove(entitlement.featureKey)}
                t={t}
              />
            ))}
          </Box>
        ))}
      </SortableContext>
      <Box
        sx={{
          mt: 1.5, p: 2, textAlign: 'center', borderRadius: 2, border: '1.5px dashed',
          borderColor: isOver ? 'primary.main' : 'divider', color: isOver ? 'primary.main' : 'text.secondary',
        }}
      >
        <Typography variant="body2">
          {known.length === 0
            ? t('Drag a feature here, or use Add in the library', { defaultValue: 'Drag a feature here, or use Add in the library' })
            : t('Drop more features here', { defaultValue: 'Drop more features here' })}
        </Typography>
      </Box>
    </Paper>
  );
};

export default PlanCanvas;
